import type { ChapterContent, FirstGameStep } from '../../types';

/**
 * Spirit Island — Rewrite 3.0 prose manual.
 * Source: docs/perfect-quality/02_NOWE_GRY_PELNE_INSTRUKCJE/08_SPIRIT_ISLAND_PELNA_INSTRUKCJA.md
 * Individual Spirit / Power / Event / Adversary / Scenario card texts remain PARTIAL blockers.
 * ONE EXPANSION = ONE CHAPTER (internal headings only).
 */
export const spiritIslandChapters: ChapterContent[] = [
  {
    title: '1. O czym jest gra',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ten rozdział mówi, czym jest Spirit Island i jak wygląda współpraca przy stole. Bez tej ramy łatwo traktować Duchy jak osobne gracze rywalizujące o punkty, a to nie jest ta gra.',
      },
      {
        type: 'heading',
        text: 'Co to jest?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gracze wcielają się w Duchy wyspy, które bronią swojego domu przed Najeźdźcami. Duchy rozwijają Obecność na planszach wyspy, zagrywają Moce, współdziałają z Dahanami i starają się wywołać wystarczająco dużo Strachu, aby wypędzić kolonizatorów.',
      },
      {
        type: 'heading',
        text: 'Po co ta współpraca istnieje?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gra jest kooperacyjna. Wszyscy gracze zwyciężają albo przegrywają razem. Wasze Moce, Obecność i decyzje o celu mają chronić tę samą wyspę. Gdy jeden Duch traci całą Obecność z wyspy, przegrywa cała drużyna.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna gra Duchem, który dobrze niszczy Osady. Bartek ma Moc dającą Obronę przed Odwetem. W tej rundzie Anna nie „zbiera punktów dla siebie”: usuwa Osadę, która w kolejnej karcie Najeźdźców miałaby budować Miasto, a Bartek chroni krainę z Dahanami, żeby kontratak mógł dojść do skutku. Obie decyzje służą temu samemu warunkowi zwycięstwa.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: planowanie tury wyłącznie pod własną planszę Ducha i ignorowanie krain, w których partnerzy nie mają Obecności. Wyspa jest wspólna.',
      },
      {
        type: 'tip',
        text: 'Warunki zwycięstwa i porażki opisuje rozdział 2. Pełną kolejność rundy — rozdział 5.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Bronicie jednej wyspy jako zespół Duchów. P: Czy można wygrać indywidualnie? O: Nie. P: Czy Obecność partnera chroni mnie przed porażką utraty Obecności? O: Nie — jeśli Twój Duch straci całą Obecność z wyspy, przegrywa cała drużyna.',
      },
    ],
  },
  {
    title: '2. Zwycięstwo i porażka',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tu nauczysz się, kiedy partia się kończy zwycięstwem albo natychmiastową porażką. Te warunki sprawdzasz po pełnym rozpatrzeniu efektu, nie w środku niepełnej akcji.',
      },
      {
        type: 'heading',
        text: 'Co to jest zwycięstwo?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Na początku zwycięstwo wymaga usunięcia z wyspy wszystkich Odkrywców, Osad i Miast. Zdobywanie Strachu przesuwa grę na wyższe Poziomy Terroru, które łagodzą warunek zwycięstwa. Im wyższy Terror, tym mniej rodzajów Najeźdźców musisz usunąć.',
      },
      {
        type: 'heading',
        text: 'Kiedy sprawdzasz zwycięstwo?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zawsze najpierw rozpatrz bieżącą akcję lub efekt w całości, a następnie sprawdź zwycięstwo, chyba że dana karta mówi inaczej. Nie przerywaj procedury w połowie tylko dlatego, że „wygląda na wygraną”.',
      },
      {
        type: 'step-list',
        steps: [
          'Terror I: na wyspie nie może być żadnego Najeźdźcy.',
          'Terror II: nie może być Osad ani Miast.',
          'Terror III: nie może być Miast.',
          'Terror IV: natychmiastowe zwycięstwo.',
        ],
      },
      {
        type: 'heading',
        text: 'Warunki porażki',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wyspa przegrywa natychmiast, gdy spełniony zostanie którykolwiek z poniższych warunków. Nie „dogrywasz rundy” po takiej sytuacji.',
      },
      {
        type: 'step-list',
        steps: [
          'Trzeba dodać Zarazę, ale w puli nie ma znacznika.',
          'Dowolny Duch traci całą Obecność z wyspy.',
          'Zabraknie kart Najeźdźców, gdy należy dobrać kolejną.',
          'Aktywny Scenariusz, Przeciwnik lub karta Wyspy Spustoszonej wprowadza własny warunek porażki.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Jesteście na Terrorze II. Na wyspie zostały tylko Odkrywcy — żadnych Osad ani Miast. Po zakończeniu Mocy, która usunęła ostatnią Osadę, sprawdzacie zwycięstwo: warunek Terroru II jest spełniony, więc wygrywacie, mimo że Odkrywcy nadal stoją. Gdybyście byli nadal na Terrorze I, te Odkrywcy blokowałyby zwycięstwo.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: ogłoszenie zwycięstwa w środku nierozpatrzonej karty Strachu albo Mocy. Najpierw dokończ efekt, potem sprawdź warunek.',
      },
      {
        type: 'tip',
        text: 'Jeśli zwycięstwo i porażka wynikają z tego samego w pełni rozpatrzonego efektu, zastosuj oficjalne reguły kolejności dla danej sytuacji. Edge cases aktualnego FAQ: [DO WERYFIKACJI]. Strach i Poziomy Terroru — rozdział 14.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Terror łagodzi warunek zwycięstwa; porażka jest natychmiastowa przy braku Zarazy, utracie całej Obecności, braku kart Najeźdźców lub specjalnym warunku modułu. P: Czy Terror IV wymaga czyszczenia planszy? O: Nie — daje natychmiastowe zwycięstwo. P: Czy utrata Obecności jednego Ducha kończy partię? O: Tak, natychmiast.',
      },
    ],
  },
  {
    title: '3. Najważniejsze elementy',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zanim rozłożysz grę, musisz rozpoznać, co leży na stole i do czego służy. Ten rozdział nie zastępuje katalogu kart Duchów ani Mocy — te teksty pozostają na komponentach do weryfikacji.',
      },
      {
        type: 'heading',
        text: 'Co leży na stole?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Plansze wyspy są podzielone na krainy i ocean. Każdy Duch ma własną planszę z torami Obecności oraz znaczniki Obecności. Karty Mocy bywają unikalne dla Ducha albo wspólne (mniejsze i większe). Najeźdźców reprezentują Odkrywcy, Osady i Miasta. Dahanie to sojusznicy na wyspie. Zaraza oznacza trwałe uszkodzenie krainy. Talia Najeźdźców napędza Odwet, Budowę i Eksplorację. Talia Strachu oraz karta Wyspy Zdrowej/Spustoszonej mierzą presję kolonizacji. W dodatkach pojawiają się Wydarzenia oraz tokeny takie jak Bestie, Dzicz, Choroba, Konflikt, Zła Ziemia i inne komponenty modułów.',
      },
      {
        type: 'step-list',
        steps: [
          'Plansze wyspy (krainy i ocean).',
          'Plansze Duchów z torami Obecności i znaczniki Obecności.',
          'Karty Mocy: unikalne, mniejsze i większe.',
          'Odkrywcy, Osady, Miasta, Dahanie, Zaraza.',
          'Talia Najeźdźców, talia Strachu, karta Wyspy Zdrowej/Spustoszonej.',
          'W dodatkach: Wydarzenia i tokeny modułów.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przed pierwszą rundą stół wygląda tak: trzy połączone plansze wyspy z Odkrywcami, Osadami i Dahanami według nadruków; obok tor Najeźdźców z miejscami Odwet–Budowa–Eksploracja; talia Strachu z włożonymi kartami Terroru; karta Wyspy Zdrowej z pulą Zarazy; u każdego gracza plansza Ducha, unikalne Moce i znaczniki Obecności. Nikt nie czyta jeszcze efektów wspólnych Mocy „z pamięci” — tekst jest na kartach.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: traktowanie Obecności jak jednostki bojowej. Sama Obecność nie blokuje Najeźdźców i nie zadaje obrażeń — daje zasięg i otwiera tory na planszy Ducha.',
      },
      {
        type: 'tip',
        text: 'Teksty indywidualnych Duchów, Mocy, Przeciwników, Scenariuszy i Wydarzeń wymagają katalogu z komponentów i nie mają statusu ready. Przygotowanie — rozdział 4.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Rozpoznajesz plansze, Obecność, Moce, Najeźdźców, Dahan, Zarazę oraz talie Najeźdźców i Strachu. P: Czy ocean jest zwykłą krainą? O: Nie, chyba że Duch lub efekt traktuje go inaczej. P: Czy aplikacja zawiera pełne teksty wszystkich Duchów? O: Nie — to jawny blocker katalogu.',
      },
    ],
  },
  {
    title: '4. Przygotowanie podstawowe',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przygotowanie ustala mapę, tor Najeźdźców, talię Strachu, pulę Zarazy i start Duchów. Błąd w setupie — zwłaszcza przy Zarazie — psuje całą partię.',
      },
      {
        type: 'heading',
        text: 'Jak przebiega przygotowanie?',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Wybierz liczbę plansz wyspy równą liczbie graczy i połącz je zgodnie z wybranym układem.',
          'Umieść Najeźdźców i Dahan według ikon wydrukowanych na planszach.',
          'Przygotuj tor Najeźdźców: Odwet, Budowa i Eksploracja.',
          'Zbuduj talię Najeźdźców z kart etapów I, II i III zgodnie z wymaganym składem.',
          'Przygotuj talię Strachu, rozdzielając ją kartami Poziomu Terroru.',
          'Umieść kartę Wyspy Zdrowej właściwą dla liczby graczy i pulę Zarazy. Stosuj aktualną korektę: do puli dodaje się o 1 Zarazę więcej niż wynikałoby z samego mnożnika liczby graczy.',
          'Każdy wybiera Ducha, bierze planszę, karty unikalnych Mocy i znaczniki Obecności.',
          'Wykonaj indywidualne przygotowanie Ducha (tekst z planszy Ducha).',
          'Dla pierwszej gry nie używaj Przeciwnika ani Scenariusza. Użyj Duchów o niskiej złożoności.',
          'Przy dodatkach przygotuj tokeny, talię Wydarzeń i pozostałe elementy zgodnie z aktywnymi modułami.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład kontroli gotowości',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Grają trzy osoby. Na stole leżą trzy plansze wyspy z figurkami według nadrukowanych ikon. Tor Najeźdźców ma miejsca na Odwet, Budowę i Eksplorację. Pula Zarazy ma o 1 znacznik więcej, niż wynikałoby z samego mnożnika liczby graczy. Nikt nie wyłożył jeszcze Przeciwnika ani Scenariusza — to pierwsza partia.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: zapomnienie o dodatkowej Zarazie z aktualnej korekty setupu. Druga typowa pomyłka to pominięcie indywidualnego przygotowania Ducha z jego planszy.',
      },
      {
        type: 'tip',
        text: 'Szczegółowy skład talii Najeźdźców i dokładne liczby znaczników zależą od komponentów i liczby graczy — odczytaj je z pudełka, jeśli lokalny katalog ich nie wymienia. Pierwsza partia — rozdział 21 i First Game.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Setup łączy mapę, Najeźdźców, Strach, Zarazę (+1 korekta) i start Duchów. P: Czy w pierwszej grze używać Wydarzeń? O: Nie — dodaj je w kolejnej partii. P: Czy przygotowanie Ducha jest wspólne dla wszystkich? O: Nie — każdy wykonuje instrukcję ze swojej planszy.',
      },
    ],
  },
  {
    title: '5. Struktura rundy',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Runda Spirit Island ma sztywny zegar. Najpierw Duchy rosną i zagrywają Moce, potem działają szybkie efekty, potem Najeźdźcy, potem wolne Moce, a na końcu Upływ Czasu. Pomylenie kolejności faz psuje Obronę, Strach i timing zniszczeń.',
      },
      {
        type: 'heading',
        text: 'Jak przebiega runda?',
        level: 2,
      },
      {
        type: 'diagram',
        diagramId: 'procedure-flow',
        alt: 'Kolejność faz rundy Spirit Island',
        fallbackText:
          'Faza Duchów (Wzrost, Energia, zagrywanie) → Szybkie Moce → Faza Najeźdźców (Wyspa Spustoszona, Wydarzenie od r.2, Strach, Odwet, Budowa, Eksploracja, przesunięcie kart) → Wolne Moce → Upływ Czasu.',
      },
      {
        type: 'heading',
        text: 'A. Faza Duchów',
        level: 3,
      },
      {
        type: 'paragraph',
        text: 'Każdy Duch wybiera jedną opcję Wzrostu, otrzymuje Energię wskazaną przez odsłonięte pole toru Energii, wybiera karty Mocy do zagrania i płaci ich koszt. Gracze mogą działać równocześnie, ale wszystkie wybory muszą być legalne.',
      },
      {
        type: 'heading',
        text: 'B. Faza Szybkich Mocy',
        level: 3,
      },
      {
        type: 'paragraph',
        text: 'Rozpatrzcie zagrane Moce oznaczone jako szybkie, w dowolnie ustalonej kolejności. To moment na Obronę i efekty, które mają zdążyć przed Odwetem, Budową lub Eksploracją.',
      },
      {
        type: 'heading',
        text: 'C. Faza Najeźdźców',
        level: 3,
      },
      {
        type: 'paragraph',
        text: 'W zależności od użytych dodatków i stanu gry wykonujecie kroki w tej kolejności:',
      },
      {
        type: 'step-list',
        steps: [
          'Sprawdź kartę Wyspy Spustoszonej.',
          'Od drugiej rundy rozpatrz Wydarzenie (gdy moduł jest aktywny).',
          'Rozpatrz zdobyte karty Strachu.',
          'Odwet.',
          'Budowa.',
          'Eksploracja.',
          'Przesuń karty Najeźdźców w lewo.',
        ],
      },
      {
        type: 'heading',
        text: 'D. Faza Wolnych Mocy',
        level: 3,
      },
      {
        type: 'paragraph',
        text: 'Rozpatrzcie zagrane wolne Moce. Często służą one do sprzątania po Najeźdźcach lub przygotowania pozycji na następną rundę.',
      },
      {
        type: 'heading',
        text: 'E. Upływ Czasu',
        level: 3,
      },
      {
        type: 'step-list',
        steps: [
          'Odrzuć wszystkie zagrane w tej rundzie karty Mocy.',
          'Usuń znaczniki jednorundowych efektów.',
          'Odwróć lub przywróć elementy zgodnie z ich zasadami.',
          'Przejdź do kolejnej rundy.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład rytmu',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Celia zagrywa szybką Moc z Obrony 3 na krainę, w której za chwilę będzie Odwet. W Fazie Najeźdźców Odwet liczy obrażenia po Obronie, więc Zaraza może w ogóle nie spaść. Gdyby Celia zagrała tę samą Obronę jako wolną Moc, Odwet zdążyłby już uderzyć.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: rozpatrywanie wolnych Mocy przed Odwetem albo Wydarzenia w pierwszej rundzie.',
      },
      {
        type: 'tip',
        text: 'Szczegóły Wzrostu — rozdział 6; Dahan — 10; Odwetu — 11; Budowy — 12; Eksploracji — 13; Wydarzeń — rozdział Zarośla i Szpony.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Duchy → Szybkie Moce → Najeźdźcy → Wolne Moce → Upływ Czasu. P: Czy szybkie i wolne Moce wolno mieszać w jednej fazie? O: Nie — każda grupa ma własną fazę. P: Czy Upływ Czasu odrzuca też nieziagrane karty z ręki? O: Nie — odrzucasz zagrane w tej rundzie karty Mocy.',
      },
    ],
  },
  {
    title: '6. Wzrost i Obecność',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wzrost to rozwój Ducha na początku rundy. Obecność na wyspie jest Twoim zasięgiem i odsłania tory na planszy Ducha. Bez Obecności nie celujesz Mocami i ryzykujesz porażkę utraty całej Obecności.',
      },
      {
        type: 'heading',
        text: 'Co to jest Wzrost?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wzrost reprezentuje rozwój Ducha. Opcje na planszy mogą pozwalać między innymi: dodać Obecność, dobrać kartę Mocy, odzyskać zagrane karty, zyskać Energię, przesunąć Obecność albo wykonać specjalną czynność. Wybierasz jedną opcję Wzrostu w Fazie Duchów.',
      },
      {
        type: 'heading',
        text: 'Energia — po co i kiedy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Energia opłaca koszt zagrywanych Mocy. W Fazie Duchów, po wyborze opcji Wzrostu, każdy Duch otrzymuje Energię wskazaną przez aktualnie odsłonięte pole toru Energii na swojej planszy. Dopiero potem wybierasz karty do zagrania i płacisz ich łączny koszt Energii. Opcja Wzrostu sama też może dawać dodatkową Energię — wtedy stosujesz dokładnie tekst wybranej opcji.',
      },
      {
        type: 'paragraph',
        text: 'Gdy zdejmujesz znacznik Obecności z toru Energii, nowo odsłonięte pole od tej chwili ustala Twój dochód Energii. Nie zgaduj wartości z pamięci innego Ducha — czytaj własne odsłonięte pole.',
      },
      {
        type: 'heading',
        text: 'Jak dodajesz Obecność?',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Wybierz znacznik z toru na planszy Ducha.',
          'Umieść go w legalnej krainie w podanym zasięgu.',
          'Odkryte pole toru od tej chwili zmienia dochód Energii, liczbę zagrywanych kart albo daje efekt specjalny.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Obecność może współdzielić krainę z innymi elementami. Sama Obecność nie blokuje Najeźdźców ani nie zadaje obrażeń. Święte Miejsce to kraina z co najmniej 2 Obecnościami tego samego Ducha, chyba że plansza Ducha zmienia tę definicję.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dawid ma jedną Obecność w lesie. Wybiera opcję Wzrostu „dodaj Obecność w zasięgu 1” i kładzie drugi znacznik w sąsiedniej krainie górskiej, zdejmując go z toru Energii. Od tej chwili korzysta z nowo odsłoniętego pola Energii — w tej samej Fazie Duchów najpierw bierze dochód z tego pola, potem płaci za zagrane Moce. Ta sama góra z dwiema jego Obecnościami staje się Świętym Miejscem — Obecność Anny w tej krainie nie liczy się do jego Świętego Miejsca.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: liczenie Obecności innych Duchów jako własnego Świętego Miejsca. Drugi błąd: zagrywanie Mocy przed odebraniem Energii z toru albo pomylenie kolejności Wzrost → Energia → zagrywanie.',
      },
      {
        type: 'tip',
        text: 'Zasięg i celowanie Mocy — rozdział 8. Utrata całej Obecności kończy grę porażką — rozdział 2. Limit liczby zagrywanych kart też bierze się z odsłoniętego toru — rozdział 7.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Jedna opcja Wzrostu na rundę; potem Energia z toru; Obecność daje zasięg i odsłania tory. P: Czy Obecność zadaje obrażenia? O: Nie sama z siebie. P: Ile Obecności trzeba do Świętego Miejsca? O: Zwykle co najmniej 2 tego samego Ducha, chyba że plansza mówi inaczej. P: Kiedy biorę Energię z toru? O: W Fazie Duchów po Wzroście, przed zagrywaniem Mocy.',
      },
    ],
  },
  {
    title: '7. Karty Mocy',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Moce to główne narzędzie Duchów. Ten rozdział uczy, co czytać na karcie, jak je zagrywać i jak rozpatrywać — bez zgadywania tekstu konkretnej Mocy z katalogu.',
      },
      {
        type: 'heading',
        text: 'Co określa karta?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Każda karta Mocy określa szybkość (szybka albo wolna), koszt Energii, zasięg, legalny cel, efekt, elementy oraz czasem progi elementów. Tekst efektu odczytujesz z karty; aplikacja nie zastępuje niezweryfikowanego katalogu Mocy.',
      },
      {
        type: 'heading',
        text: 'Zagrywanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W Fazie Duchów wybierz maksymalnie tyle kart, ile pozwala odsłonięty tor zagrywanych kart. Zapłać łączny koszt Energii. Karty leżą zagrane do odpowiednich faz rozpatrywania.',
      },
      {
        type: 'heading',
        text: 'Rozpatrywanie',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Sprawdź zasięg z jednej z własnych Obecności.',
          'Sprawdź wymagania celu.',
          'Wybierz jeden legalny cel.',
          'Wykonaj instrukcje karty od góry do dołu.',
          'Zastosuj progi elementów, jeśli posiadasz wymagane elementy.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Elementy nie są zużywane. Ta sama ikona może spełnić wymagania wielu Mocy i progów w tej samej rundzie. Nie wolno zmienić celu po rozpoczęciu rozpatrywania, chyba że efekt wyraźnie na to pozwala. Jeśli nie ma legalnego celu, Mocy nie można rozpatrzyć — zagraną kartę nadal uważa się za zagraną.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ewa ma odsłonięty tor pozwalający zagrać 2 karty i 4 Energii. Zagrywa szybką Moc za 1 Energię i wolną za 2. W Fazie Szybkich Mocy celuje w krainę w zasięgu 0 (z własną Obecnością), spełnia warunek „kraina z Dahan” i wykonuje efekt. Potem sprawdza próg elementów: ma wymagane symbole z zagranych kart i toru, więc dodaje efekt progu. Elementów nie odrzuca ani nie „wydaje”.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: zużywanie elementów po sprawdzeniu progu. Elementy są symbolami posiadanymi w rundzie, nie zasobem do wydania.',
      },
      {
        type: 'tip',
        text: 'Zasięg i ograniczenia celu — rozdział 8. Timing szybkich i wolnych Mocy — rozdział 5.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Płać Energię, celuj legalnie, czytaj od góry, progi bez zużywania elementów. P: Czy jedna karta może spełnić kilka progów? O: Tak, jeśli masz wymagane elementy. P: Czy wolno zmienić cel w trakcie efektu? O: Nie, chyba że karta na to pozwala.',
      },
    ],
  },
  {
    title: '8. Zasięg i celowanie',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zasięg mówi, dokąd Moce mogą sięgnąć. Błędne liczenie sąsiedztwa albo oceanu to jeden z najczęstszych sporów przy stole.',
      },
      {
        type: 'heading',
        text: 'Jak liczysz zasięg?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zasięg liczysz pomiędzy krainami po sąsiedztwie. Kraina z Obecnością ma zasięg 0 względem tej Obecności. Ocean nie jest zwykłą krainą, chyba że aktywny Duch lub efekt traktuje go inaczej.',
      },
      {
        type: 'heading',
        text: 'Ograniczenia celu',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Oprócz zasięgu karta może wymagać konkretnego rodzaju terenu, obecności albo braku Najeźdźców, Dahan, własnej Obecności, Świętego Miejsca albo krainy przybrzeżnej bądź śródlądowej. Cel musi być legalny w całości — nie wolno wybrać krainy „prawie pasującej”.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Filip ma Obecność w krainie A. Moc ma zasięg 1 i cel „kraina z Osadą”. Kraina B sąsiaduje z A i ma Osadę — jest legalna. Kraina C jest w zasięgu 1, ale nie ma Osady — nie jest legalna. Ocean sąsiadujący z A nie jest celem, bo ocean nie jest zwykłą krainą dla tej Mocy.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: wybór celu bez spełnienia wszystkich warunków albo próba „pustego” celu, który łamie wymagania karty.',
      },
      {
        type: 'tip',
        text: 'Można wybrać cel bez efektu tylko wtedy, gdy cel jest legalny. Brak legalnego celu oznacza, że Mocy nie rozpatrujesz, choć karta pozostaje zagrana.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Sąsiedztwo krain, zasięg 0 = kraina z Obecnością, ocean osobno. P: Czy Obecność partnera daje mi zasięg 0? O: Nie — liczysz od własnej Obecności. P: Czy Izolacja może zmienić sąsiedztwo? O: Tak, zgodnie z efektem — szczegóły w rozdziale Rozdarta Ziemia.',
      },
    ],
  },
  {
    title: '9. Najeźdźcy',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Najeźdźcy to przeciwnik systemu, nie gracza po drugiej stronie stołu. Musisz znać ich zdrowie, obrażenia i to, kiedy generują Strach.',
      },
      {
        type: 'heading',
        text: 'Rodzaje Najeźdźców',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Odkrywca ma 1 Zdrowia i zadaje 1 obrażenie. Osada ma 2 Zdrowia i zadaje 2 obrażenia. Miasto ma 3 Zdrowia i zadaje 3 obrażenia. Te wartości używasz przy Odwecie i przy Moce zadających obrażenia Najeźdźcom.',
      },
      {
        type: 'heading',
        text: 'Strach i obrażenia',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zniszczenie Osady generuje 1 Strach, a Miasta 2 Strachu. Samo zadanie obrażeń bez zniszczenia nie generuje Strachu. Obrażenia zadane Najeźdźcom utrzymują się tylko do końca bieżącej akcji. Jeśli element nie został zniszczony, po zakończeniu akcji wraca do pełnego Zdrowia.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Moc Anny zadaje 2 obrażenia Miastu (3 Zdrowia). Miasto nie zostaje zniszczone, więc po tej akcji wraca do pełnych 3 Zdrowia i nie generuje Strachu. Późniejsza osobna Moc musi znów liczyć pełne zdrowie. Gdyby Anna zadała 3 obrażenia i zniszczyła Miasto, drużyna dostałaby 2 Strachu.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: pozostawianie obrażeń na Najeźdźcach między akcjami. Każda nowa akcja zaczyna od pełnego Zdrowia, jeśli poprzednia nie zniszczyła figurki.',
      },
      {
        type: 'tip',
        text: 'Przesunięcie Najeźdźcy nie niszczy go — zmienia tylko krainę. Rozróżnienie „zniszcz”, „usuń” i „zastąp” zależy od czasownika na karcie; pełny katalog terminów: [DO WERYFIKACJI].',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: '1/2/3 Zdrowia i obrażeń; Strach tylko za zniszczenie Osady lub Miasta; obrażenia nie przenoszą się między akcjami. P: Czy Odkrywca generuje Strach przy zniszczeniu? O: Źródło nie przypisuje mu Strachu za zniszczenie — Strach podano dla Osady i Miasta. P: Czy usunięcie bez „zniszczenia” zawsze daje Strach? O: Zależy od czasownika efektu.',
      },
    ],
  },
  {
    title: '10. Dahanie',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ten rozdział jest samodzielnym miejscem nauki o Dahanach: kim są w strukturze gry, jak trafiają na mapę, jak Duch ich zbiera i wypycha Mocami, jak przeżywają Odwet, jak rozdzielasz obrażenia, kiedy kontratakują oraz jak łączą się z Obroną i Wydarzeniami. Bez tej ramy łatwo traktować ich jak „kolejnych Najeźdźców” albo jak graczy z własną turą — a nie są ani jednym, ani drugim.',
      },
      {
        type: 'heading',
        text: 'Kim są Dahanie i po co istnieją?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dahanie to rdzenni mieszkańcy wyspy i sojusznicy Duchów. Stoją na tych samych krainach co Najeźdźcy, Zaraza i Obecność, ale nie należą do talii Najeźdźców i nie wykonują Eksploracji, Budowy ani Odwetu. Ich rola w rozgrywce jest podwójna: podczas Odwetu są celem przemocy kolonizacji, a jeśli przetrwają obrażenia, odpowiadają kontratakiem, który może zniszczyć budynki i wygenerować Strach. Moce i Wydarzenia często wymagają obecności Dahan albo ich przemieszczają — dlatego utrzymanie żywych sojuszników w dobrych krainach jest decyzją strategiczną całego stołu, nie ozdobą mapy.',
      },
      {
        type: 'heading',
        text: 'Dahanie nie mają własnej tury',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W kolejności rundy nie ma fazy „tura Dahan”. Runda to Faza Duchów, Szybkie Moce, Faza Najeźdźców, Wolne Moce i Upływ Czasu. Dahanie działają tylko wtedy, gdy konkretna reguła albo efekt ich wywołuje: Odwet zadaje im obrażenia i ewentualnie pozwala na kontratak; Moc może ich Zbierać, Wypychać albo wymagać ich w celu; Wydarzenie może mieć sekcję Dahan na końcu karty. Nie planujesz „ruchu Dahan w ich turze” — planujesz Moce i Obronę tak, by byli we właściwym miejscu, gdy system albo karta ich dotyczy.',
      },
      {
        type: 'heading',
        text: 'Przygotowanie — skąd biorą się na planszy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Podczas przygotowania podstawowego umieszczasz Najeźdźców i Dahan według ikon wydrukowanych na planszach wyspy. Nie wymyślasz startowej liczby ani pozycji: ikona na krainie mówi, ile figurek Dahan tam stoi na początek. Później ich liczba i rozmieszczenie zmieniają się wyłącznie przez efekty Mocy, Wydarzeń, kart Strachu albo przez zniszczenie w walce — nigdy przez samodzielny ruch „bo tak chcą”.',
      },
      {
        type: 'heading',
        text: 'Zbieranie i Wypychanie (Gather / Push)',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wiele Mocy używa słów kluczowych Zbierz (Gather) i Wypchnij (Push), by przemieszczać Dahan (albo inne figurki wskazane na karcie). To nie jest osobna akcja gracza poza tekstem efektu: najpierw sprawdzasz zasięg i legalny cel Mocy dokładnie tak jak w rozdziale 8, potem wykonujesz instrukcję zbierania albo wypychania zgodnie z kartą.',
      },
      {
        type: 'paragraph',
        text: 'Zbierz do wskazanej krainy oznacza: wybierasz do podanej liczby figurek danego typu w krainach sąsiadujących z celem i przenosisz je do tej krainy-celu. Zasięg Mocy mierzysz do krainy, do której zbierasz — nie do każdej krainy źródłowej osobno. Wypchnij z wskazanej krainy oznacza: wybierasz do podanej liczby figurek w tej krainie i każdą przenosisz do sąsiedniej krainy; różne figurki mogą trafić do różnych sąsiadów, o ile efekt nie mówi inaczej. Ocean nie jest zwykłą krainą do zbierania ani wypychania, chyba że aktywny efekt traktuje go inaczej. Izolacja zwykle nie blokuje przemieszczania Dahan — szczegóły timingowe Izolacji pozostają przy tekście efektu.',
      },
      {
        type: 'heading',
        text: 'Zdrowie, rozdzielanie obrażeń i ranni po akcji',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Każdy Dahan ma 2 Zdrowia. Gdy efekt (najczęściej Odwet) zadaje obrażenia Dahanom w krainie, rozdzielasz je w dozwolony sposób: tak, aby zniszczyć jak najwięcej Dahan. Każde pełne 2 obrażenia niszczą jednego Dahan; nie wolno rozproszyć obrażeń po 1 na kilku figurkach tylko po to, by uniknąć zniszczenia. Nadmiar 1 obrażenia po maksymalnym zniszczeniu oznacza jednego rannego Dahan (oznacz figurkę — np. odwróć). Zniszczone figurki usuń z planszy. Ocalali — w tym ranni — liczą się do kontrataku w tym samym Odwecie.',
      },
      {
        type: 'paragraph',
        text: 'Ranni Dahanie pozostają oznaczeni do Upływu Czasu na końcu rundy; wtedy wracają do pełnego Zdrowia. To inny timing niż u Najeźdźców: obrażenia na Najeźdźcach resetują się już na końcu bieżącej akcji (rozdział 9). Nie myl tych dwóch resetów. Zniszczeni Dahanie nie wracają sami z siebie — tylko jeśli późniejszy efekt ich ponownie doda lub przywróci.',
      },
      {
        type: 'heading',
        text: 'Odwet, Obrona i kontratak',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Podczas Odwetu w wskazanej krainie sumujesz obrażenia wszystkich Najeźdźców, odejmujesz Obronę działającą w tej krainie, a pozostały pakiet zadajesz jednocześnie krainie i Dahanom. Ta sama wartość Obrony chroni i krainę (próg Zarazy), i Dahan — nie wybierasz „bronię tylko ludzi”. Obrona z Mocy lub Wydarzeń działa na ten pakiet tylko wtedy, gdy efekt mówi o obrażeniach Najeźdźców podczas Odwetu albo wyraźnie rozszerza działanie.',
      },
      {
        type: 'step-list',
        steps: [
          'Zsumuj obrażenia Najeźdźców w krainie Odwetu.',
          'Odejmij Obronę działającą w tej krainie.',
          'Pozostałe obrażenia zadaj jednocześnie krainie i Dahanom.',
          'Przy ≥2 obrażeniach krainy dodaj 1 Zarazę (nie więcej z samej siły ciosu).',
          'Rozdziel obrażenia na Dahan tak, by zniszczyć ich jak najwięcej (po 2 na figurkę); ewentualny 1 nadmiaru → jeden ranny; usuń zniszczonych.',
          'Jeśli pozostał co najmniej jeden Dahan, każdy ocalały (także ranny) zadaje 2 obrażenia Najeźdźcom; drużyna wspólnie rozdziela te obrażenia między Najeźdźców.',
          'Zniszczenie Osady w kontrataku generuje 1 Strach, Miasta — 2 Strachu; samo zranienie bez zniszczenia nie generuje Strachu.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Kontratak następuje dopiero po obrażeniach. Nie kontratakujesz „na zapas” przed ciosem Najeźdźców. Liczba obrażeń kontrataku to 2 × liczba ocalałych Dahan — ranny Dahan, który nie został zniszczony, nadal zadaje pełne 2. Pełna procedura Odwetu i Zarazy: rozdziały 11 i 15.',
      },
      {
        type: 'heading',
        text: 'Wydarzenia — sekcja Dahan',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gdy gra z dodatkiem używa Wydarzeń, od drugiej rundy w Fazie Najeźdźców dobierasz kartę i rozpatruje się sekcje w kolejności podanej na karcie. Na końcu wykonujesz sekcje Bestii i Dahan. To zweryfikowany timing ze źródła: sekcja Dahan jest częścią procedury Wydarzenia, a nie osobną turą sojuszników. Treści konkretnych kart Wydarzeń pozostają przy komponentach — [DO WERYFIKACJI: katalog Wydarzeń]. Nie zgaduj efektu sekcji Dahan z pamięci; przeczytaj kartę.',
      },
      {
        type: 'heading',
        text: 'Zniszcz, usuń, zastąp — zweryfikowany zakres',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przy obrażeniach Odwetu źródło mówi wprost: zniszczone figurki Dahan usuń. Przy Najeźdźcach zniszczenie Osady lub Miasta generuje Strach, a samo zadanie obrażeń bez zniszczenia — nie. Przesunięcie (Zbierz / Wypchnij / inne przesunięcie) zmienia krainę i samo w sobie nie jest zniszczeniem. Pełne rozróżnienie czasowników „zniszcz”, „usuń” i „zastąp” na wszystkich kartach Mocy i Wydarzeń pozostaje [DO WERYFIKACJI: katalog terminów] — przy sporze o Strach lub powrót figurki trzymaj się dokładnego czasownika na komponencie.',
      },
      {
        type: 'heading',
        text: 'Przykład A — Odwet, Obrona, dwóch Dahan, kontratak i Strach',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W krainie Odwetu stoją Osada i Odkrywca (2 + 1 = 3 obrażenia) oraz dwóch Dahan. Szybka Moc dała tej krainie Obronę 1. Po Obronie zostaje 2 obrażenia — ten sam pakiet trafia jednocześnie w krainę i w Dahan. Kraina dostaje 1 Zarazę (≥2). Na Dahanach 2 obrażenia muszą zniszczyć jednego Dahan (nie wolno dać po 1 obu, by obu ocalić). Drugi Dahan wychodzi bez obrażeń i zostaje ocalały. Kontratak: 1 × 2 = 2 obrażenia. Drużyna kieruje oba w Osadę (2 Zdrowia) — Osada zostaje zniszczona i generuje 1 Strach. Odkrywca zostaje. Gdyby Obrony nie było, 3 obrażenia zniszczyłyby jednego Dahan i zraniły drugiego (2 + 1); ranny nadal kontratakowałby za 2, a ranę zdjąłby dopiero Upływ Czasu.',
      },
      {
        type: 'heading',
        text: 'Przykład B — Zbierz lub Wypchnij Dahan Mocą, zasięg i legalny cel',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ola ma Obecność w krainie A. Rozpatruje Moc o zasięgu 1: cel musi być krainą z co najmniej jednym Dahan, a efekt każe Zebrać do 2 Dahan do tej krainy (konkretny tekst czytasz z karty — tu tylko procedura). Kraina B sąsiaduje z A, ma jednego Dahan i leży w zasięgu 1 — to legalny cel. Ola wybiera B, potem zbiera: w sąsiedniej do B krainie C stoją dwaj Dahanie, w sąsiedniej D — żaden. Przenosi obu z C do B. Po efekcie w B są trzech Dahan, a C jest puste z Dahan. Kraina E jest w zasięgu 1 od A, ale nie ma Dahan — nie jest legalnym celem, więc nie wolno „najpierw wybrać E, a potem zbierać, żeby spełnić warunek”. Zasięg sprawdzasz do celu przed zbieraniem; zbieranie dotyczy wyłącznie sąsiadów celu i limitu z karty.',
      },
      {
        type: 'paragraph',
        text: 'Wariant Wypchnij: ta sama Ola celuje w B (nadal legalny cel z Dahan) Mocą, która każe Wypchnąć do 2 Dahan z celu. W B stoją trzech Dahan; Ola wypycha dwóch — jednego do sąsiedniej C, drugiego do sąsiedniej F (różne sąsiedztwa są dozwolone, o ile efekt nie mówi inaczej). Nie wypycha do oceanu ani poza mapę. Wzmocnienie zasięgu Mocy nie wydłuża dystansu zbierania ani wypychania: nadal tylko sąsiedzi celu.',
      },
      {
        type: 'heading',
        text: 'Najczęstsze błędy',
        level: 2,
      },
      {
        type: 'warning',
        text: 'Typowe błędy: dawanie Dahanom własnej tury ruchu; kontratak przed obrażeniami Odwetu; traktowanie Obrony jako chroniącej tylko Dahan albo tylko krainę; rozpraszanie obrażeń po 1 na wielu Dahan, by uniknąć zniszczenia; resetowanie ran Dahan po każdej akcji jak u Najeźdźców zamiast w Upływie Czasu; Zbieranie z krain nie sąsiadujących z celem albo mierzenie zasięgu do źródła zamiast do celu; wymyślanie tekstu sekcji Dahan na Wydarzeniu zamiast czytania karty; mylenie przesunięcia z zniszczeniem przy Strachu.',
      },
      {
        type: 'tip',
        text: 'Powiązania: Odwet i Obrona — rozdział 11; zasięg i cel — rozdział 8; Strach za zniszczenie budynków — rozdział 9 i 14; Wydarzenia i sekcja Dahan — rozdział Zarośla i Szpony; Zaraza z Odwetu — rozdział 15; Upływ Czasu — rozdział 5 i 20.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dahanie to sojusznicy bez własnej tury: setup z ikon, ruch przez Zbierz/Wypchnij na Moce, Odwet rani ich po Obronie (niszcząc maksymalnie wielu), ocalali kontratakują po 2, ranni leczą się w Upływie Czasu. P: Czy Dahanie budują lub eksplorują? O: Nie — to akcje Najeźdźców. P: Czy ranny Dahan (1 obrażenie) kontratakuje? O: Tak, jeśli nie został zniszczony — zadaje 2. P: Czy wolno dać po 1 obrażeniu dwóm Dahan zamiast zniszczyć jednego? O: Nie — niszczysz jak najwięcej. P: Czy Obrona obniża osobno cios w Dahan i w krainę? O: Nie — jeden pakiet po Obronie idzie jednocześnie w obu. P: Skąd wziąć tekst sekcji Dahan na Wydarzeniu? O: Z karty; katalog jest [DO WERYFIKACJI].',
      },
    ],
  },
  {
    title: '11. Odwet',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Odwet to moment, w którym Najeźdźcy uderzają w krainę i Dahan. Tu najczęściej padają Zaraza, błędna Obrona i zły timing kontrataku. Pełny portret sojuszników — rozdział 10. Dahanie.',
      },
      {
        type: 'heading',
        text: 'Po co istnieje Odwet?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Odwet reprezentuje przemoc kolonizacji wobec wyspy i jej mieszkańców. Obrona, Konflikt i szybkie Moce istnieją między innymi po to, by ten cios osłabić zanim spadnie.',
      },
      {
        type: 'heading',
        text: 'Jak przebiega?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dla każdej krainy typu wskazanego na karcie Odwetu: zsumuj obrażenia wszystkich Najeźdźców w tej krainie, odejmij Obronę działającą w tej krainie, a pozostałe obrażenia zadaj jednocześnie krainie i Dahanom.',
      },
      {
        type: 'heading',
        text: 'Obrażenia krainy',
        level: 3,
      },
      {
        type: 'paragraph',
        text: 'Jeśli po Obronie kraina otrzymuje co najmniej 2 obrażenia, dodaj do niej 1 Zarazę. Nie dodawaj większej liczby Zarazy tylko dlatego, że obrażeń było więcej — jeden Odwet w krainie daje co najwyżej tę jedną Zarazę z tej reguły.',
      },
      {
        type: 'heading',
        text: 'Obrażenia Dahan i kontratak',
        level: 3,
      },
      {
        type: 'paragraph',
        text: 'Każdy Dahan ma 2 Zdrowia. Rozdziel obrażenia tak, by zniszczyć jak najwięcej Dahan, i usuń zniszczone figurki; ewentualny 1 nadmiaru oznacza rannego do Upływu Czasu. Jeśli po obrażeniach pozostał co najmniej jeden Dahan, każdy ocalały (także ranny) zadaje 2 obrażenia Najeźdźcom. Gracze wspólnie rozdzielają te obrażenia między Najeźdźców. Pełna nauka, Zbieranie/Wypychanie i przykłady A/B — rozdział 10.',
      },
      {
        type: 'heading',
        text: 'Pełny przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W krainie są Osada i Odkrywca, czyli łącznie 3 obrażenia. Obrona 2 zmniejsza je do 1. Kraina nie otrzymuje Zarazy (potrzeba co najmniej 2), a obecny Dahan otrzymuje 1 obrażenie. Następnie ten Dahan kontratakuje za 2 obrażenia, które drużyna rozdziela na Najeźdźców. Bogatszy wariant z dwoma Dahan, Zarazą i Strachem za zniszczenie Osady — przykład A w rozdziale 10.',
      },
      {
        type: 'warning',
        text: 'Typowe błędy: dodawanie wielu Zaraz za jeden bardzo silny Odwet; kontratak Dahan przed obrażeniami; mylenie Obrony z efektem, który nie działa na obrażenia Odwetu.',
      },
      {
        type: 'tip',
        text: 'Ta sama wartość Obrony redukuje łączny pakiet obrażeń Najeźdźców przed zadaniem go krainie i Dahanom. Obrona z Mocy lub Wydarzeń działa na Odwet tylko jeśli efekt mówi, że są to obrażenia Najeźdźców podczas Odwetu albo wyraźnie rozszerza działanie. Zaraza — rozdział 15. Dahanie — rozdział 10.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Suma obrażeń − Obrona → kraina i Dahanie jednocześnie → potem kontratak ocalałych. P: Czy 5 obrażeń po Obronie daje kilka Zaraz? O: Nie — przy ≥2 dodajesz 1 Zarazę. P: Czy Obrona chroni tylko Dahan? O: Nie — ten sam pakiet dotyczy też krainy.',
      },
    ],
  },
  {
    title: '12. Budowa',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Budowa powiększa obecność kolonii. Jeśli nie zatrzymasz jej Szybkimi Mocami lub tokenami, Osady i Miasta rosną tam, gdzie już stoją Najeźdźcy.',
      },
      {
        type: 'heading',
        text: 'Jak przebiega Budowa?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W każdej wskazanej krainie zawierającej Najeźdźców: jeśli jest więcej Miast niż Osad, dodaj Miasto; w przeciwnym razie dodaj Osadę. Efekty Przeciwników mogą zmieniać tę procedurę — wtedy obowiązuje tekst Przeciwnika.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Karta Budowy wskazuje lasy. W lesie stoją 1 Osada i 0 Miast — dodajecie Osadę. W innym lesie stoją 2 Miasta i 1 Osada — jest więcej Miast niż Osad, więc dodajecie Miasto.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: budowanie w krainie bez Najeźdźców albo pomylenie reguły „więcej Miast niż Osad”.',
      },
      {
        type: 'tip',
        text: 'Choroba (dodatek Zarośla i Szpony) może zapobiec jednej normalnej Budowie w krainie, po czym jest usuwana; nie usuwa istniejących budynków. Szczegóły — rozdział Zarośla i Szpony.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Kraina z Najeźdźcami dostaje Osadę, chyba że Miast jest więcej niż Osad — wtedy Miasto. P: Czy pusta kraina buduje? O: Nie przy standardowej procedurze — musi zawierać Najeźdźców. P: Czy Przeciwnik może zmienić Budowę? O: Tak, jeśli jego tekst tak stanowi.',
      },
    ],
  },
  {
    title: '13. Eksploracja',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Eksploracja dokłada Odkrywców do krain, które mają źródło Najeźdźców. Źle zrozumiane źródło sprawia, że Odkrywcy pojawiają się tam, gdzie nie powinni.',
      },
      {
        type: 'heading',
        text: 'Jak przebiega Eksploracja?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dla każdej wskazanej krainy dodaj Odkrywcę, jeśli kraina ma źródło Najeźdźców: sąsiaduje z oceanem, sąsiaduje z krainą zawierającą Osadę lub Miasto, albo spełnia dodatkową regułę aktywnego Przeciwnika.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Karta Eksploracji wskazuje góry. Góra A sąsiaduje z oceanem — dostaje Odkrywcę. Góra B sąsiaduje z krainą, w której stoi Osada — też dostaje Odkrywcę. Góra C sąsiaduje tylko z krainą mającą samego Odkrywcę — nie ma źródła, więc nie dostaje nowego Odkrywcy.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: Eksploracja z sąsiedniego Odkrywcy. Źródłem jest Osada, Miasto lub ocean, nie sam Odkrywca.',
      },
      {
        type: 'tip',
        text: 'Dzicz (Zarośla i Szpony) zapobiega jednej normalnej Eksploracji w krainie i jest usuwana. Nie blokuje automatycznie Eksploracji wywołanej kartą Mocy, chyba że efekt mówi inaczej. Szczegóły — rozdział Zarośla i Szpony.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Odkrywca trafia do wskazanej krainy tylko ze źródłem: ocean, sąsiednia Osada/Miasto lub reguła Przeciwnika. P: Czy sam Odkrywca jest źródłem? O: Nie. P: Czy Dzicz działa na Moc dodającą Odkrywcę? O: Nie automatycznie.',
      },
    ],
  },
  {
    title: '14. Strach',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Strach przesuwa Poziom Terroru i odsłania karty Strachu. To główna droga do łatwiejszego zwycięstwa — i źródło efektów rozpatrywanych w Fazie Najeźdźców.',
      },
      {
        type: 'heading',
        text: 'Jak generujesz Strach?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Za każdym razem, gdy generujesz Strach, przesuń znaczniki z puli Strachu do puli wygenerowanego Strachu. Gdy wygenerujesz liczbę Strachu równą progowi zależnemu od liczby graczy: zdobądź wierzchnią kartę Strachu, odłóż ją zakrytą do rozpatrzenia, zwróć znaczniki do puli Strachu, a nadmiar Strachu przechodzi na kolejną kartę.',
      },
      {
        type: 'paragraph',
        text: 'Gdy zostanie odsłonięta karta Terroru, od tej chwili obowiązuje łagodniejszy warunek zwycięstwa opisany w rozdziale 2.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Próg Strachu przy waszej liczbie graczy wynosi 4. Macie już 3 w puli wygenerowanego Strachu i niszczycie Miasto za 2 Strachu. Przechodzicie próg: bierzecie kartę Strachu na później, wracacie znacznikami do puli, a 1 nadmiarowy Strach zostaje na kolejną kartę.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: natychmiastowe rozpatrywanie karty Strachu w chwili zdobycia zamiast we właściwym kroku Fazy Najeźdźców.',
      },
      {
        type: 'tip',
        text: 'Karty Strachu rozpatrujecie w Fazie Najeźdźców po ewentualnym Wydarzeniu, a przed Odwetem — patrz rozdział 5.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zbieraj Strach do progu, bierz kartę, nadmiar przechodzi dalej; Terror łagodzi zwycięstwo. P: Czy obrażenia bez zniszczenia dają Strach? O: Nie. P: Czy Terror IV czyści planszę? O: Nie — to natychmiastowe zwycięstwo.',
      },
    ],
  },
  {
    title: '15. Zaraza',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zaraza to trwałe uszkodzenie wyspy. Psuje Obecność Duchów i może kaskadować. Brak znacznika Zarazy w puli oznacza natychmiastową porażkę.',
      },
      {
        type: 'heading',
        text: 'Jak dodajesz Zarazę?',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Dodaj 1 znacznik z karty Wyspy.',
          'Zniszcz po 1 Obecności każdego Ducha w tej krainie.',
          'Jeśli kraina już miała Zarazę, następuje kaskada: wybierz jedną sąsiednią krainę i dodaj tam Zarazę; kaskada może się powtarzać.',
        ],
      },
      {
        type: 'heading',
        text: 'Pustoszenie — Wyspa Spustoszona',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gdy pula Wyspy Zdrowej się wyczerpie, następuje pustoszenie karty Wyspy: odwróć kartę na stronę Wyspy Spustoszonej, wykonaj jej instrukcję i przygotuj nową pulę Zarazy po tej stronie. Od tej chwili w Fazie Najeźdźców najpierw sprawdzasz kartę Wyspy Spustoszonej (przed Wydarzeniem, Strachem i Odwetem) — może wprowadzać własny warunek porażki lub inny trwały efekt. Tekst konkretnej karty Wyspy odczytujesz z komponentu; nie zgaduj wartości puli ani efektu „na logikę”.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Odwet dodaje Zarazę do krainy, w której Anna i Bartek mają po jednej Obecności. Bierzecie ostatni znacznik z puli Wyspy Zdrowej, niszczycie po jednej Obecności Anny i Bartka. Pula jest pusta — odwracacie kartę na Wyspę Spustoszoną, wykonujecie jej instrukcję i budujecie nową pulę. W kolejnej rundzie na początku Fazy Najeźdźców najpierw sprawdzacie tę kartę. Osobno: gdy kraina już miała Zarazę przed dodaniem kolejnej, wybieracie sąsiednią krainę i kaskadujecie Zarazę dalej.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: niszczenie wszystkich Obecności Ducha w krainie zamiast po jednej na Ducha. Drugi błąd: dodanie Zarazy bez znacznika w puli zamiast ogłoszenia porażki. Trzeci: pomijanie sprawdzenia Wyspy Spustoszonej na początku Fazy Najeźdźców.',
      },
      {
        type: 'tip',
        text: 'Pula startowa ma o 1 Zarazę więcej niż sam mnożnik liczby graczy — rozdział 4. Kolejność Fazy Najeźdźców — rozdział 5. Warunki porażki — rozdział 2.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dodaj znacznik, zniszcz po 1 Obecności na Ducha, kaskaduj przy drugiej Zarazie; wyczerpanie puli Zdrowa → odwróć na Spustoszoną. P: Czy Zaraza niszczy wszystkie Obecności? O: Po jednej każdego Ducha w krainie, o ile efekt nie zmienia zasady. P: Co jeśli w puli nie ma znacznika, a trzeba dodać Zarazę? O: Natychmiastowa porażka. P: Kiedy czytać Wyspę Spustoszoną? O: Na początku Fazy Najeźdźców, przed Wydarzeniem i Odwetem.',
      },
    ],
  },
  {
    title: 'Zarośla i Szpony',
    requiredAddons: ['branch-and-claw'],
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tym dodatku',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zarośla i Szpony dodają Wydarzenia oraz tokeny Bestii, Dziczy, Choroby i Konfliktu, a także nowych Duchów, Moce i Przeciwnika. Ten rozdział zbiera zasady ogólne tokenów i Wydarzeń. Teksty indywidualnych Duchów, Mocy, Wydarzeń i Przeciwnika pozostają [DO WERYFIKACJI] na podstawie komponentów — nie mają statusu ready.',
      },
      {
        type: 'heading',
        text: 'Wydarzenia — po co i kiedy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wydarzenia wprowadzają nieprzewidywalność do Fazy Najeźdźców. Nie rozpatruj Wydarzenia w pierwszej rundzie. Od drugiej rundy dobierz kartę, rozpatrz sekcje w podanej kolejności, a jeśli jest wybór zespołowy — wybierzcie opcję wspólnie i opłaćcie koszt zgodnie z kartą. Na końcu wykonaj sekcje Bestii i Dahan. Tekst Wydarzenia może zmienić reguły ogólne na czas jego rozpatrywania. Kim są Dahanie i jak walczą poza Wydarzeniem — rozdział 10; teksty sekcji Dahan na kartach: [DO WERYFIKACJI: katalog Wydarzeń].',
      },
      {
        type: 'heading',
        text: 'Dzicz',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Pierwsza normalna Eksploracja w krainie zostaje pominięta. Usuń Dzicz. Dzicz nie blokuje automatycznie Eksploracji wywołanej kartą Mocy, chyba że efekt mówi inaczej.',
      },
      {
        type: 'heading',
        text: 'Choroba',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Pierwsza normalna Budowa w krainie zostaje pominięta. Usuń Chorobę. Choroba nie usuwa istniejących budynków — tylko przerywa jedną nadchodzącą Budowę.',
      },
      {
        type: 'heading',
        text: 'Konflikt',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gdy wybrany Najeźdźca miałby zadać obrażenia podczas Odwetu, możesz usunąć Konflikt spod niego, aby anulować jego obrażenia w tej akcji. To decyzja drużyny w momencie Odwetu.',
      },
      {
        type: 'heading',
        text: 'Bestie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Bestie nie mają wrodzonego efektu. Są wykorzystywane przez Moce, Wydarzenia i karty Strachu. Nie traktuj ich jak jednostek walczących „same z siebie”.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W rundzie 2 dobieracie Wydarzenie i rozpatrujecie je przed kartami Strachu. W krainie z Chorobą następuje Budowa — pomijacie tę jedną Budowę i usuwacie Chorobę; istniejąca Osada zostaje. W innej krainie Dzicz kasuje pierwszą normalną Eksplorację i znika. Podczas Odwetu usuwacie Konflikt spod Osady, więc ta Osada nie wnosi swoich 2 obrażeń do sumy.',
      },
      {
        type: 'warning',
        text: 'Typowe błędy: Wydarzenie w rundzie 1; traktowanie Bestii jak walczących bez efektu karty; oczekiwanie, że Choroba zburzy już stojące budynki.',
      },
      {
        type: 'tip',
        text: 'Nowych Duchów, Mocy i Przeciwnika z tego dodatku kataloguj z komponentów. Setup tokenów i talii Wydarzeń wykonaj zgodnie z aktywnymi modułami — rozdział 4.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wydarzenia od rundy 2; Dzicz ↔ Eksploracja; Choroba ↔ Budowa; Konflikt ↔ obrażenia Odwetu; Bestie tylko przez karty. P: Czy Dzicz blokuje Moc eksplorującą? O: Nie automatycznie. P: Czy Konflikt anuluje cały Odwet w krainie? O: Anuluje obrażenia wybranego Najeźdźcy, spod którego usuniesz token.',
      },
    ],
  },
  {
    title: 'Rozdarta Ziemia',
    requiredAddons: ['jagged-earth'],
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tym dodatku',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Rozdarta Ziemia rozbudowuje system tokenów, wprowadza Złą Ziemię i Izolację, nowych Duchów o szerokim zakresie złożoności, nowe plansze, Moce, Wydarzenia, Scenariusze i Przeciwników. Poniżej są zasady ogólne Złej Ziemi i Izolacji ze źródła lokalnego. Pełne katalogi kart i Duchów: [DO WERYFIKACJI].',
      },
      {
        type: 'heading',
        text: 'Zła Ziemia',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zła Ziemia zwiększa obrażenia zadawane przez pojedynczą akcję Najeźdźcom i Dahanom w krainie. Nie zwiększa obrażeń zadawanych samej krainie. Przy Odwecie pamiętaj: pakiet po Obronie nadal osobno oceniasz pod kątem Zarazy dla krainy — Zła Ziemia nie podbija obrażeń krainy.',
      },
      {
        type: 'heading',
        text: 'Izolacja',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Izolacja modyfikuje sąsiedztwo i źródła Najeźdźców zgodnie z zasadami efektu, który ją stosuje. Zwykle nie blokuje przemieszczania Dahan ani używania zasięgu przez Duchy. Dokładne konsekwencje timingowe zależą od karty lub tokenu — przy sporze trzymaj się tekstu efektu; osobna pełna sekcja timingowa wymaga weryfikacji komponentów.',
      },
      {
        type: 'heading',
        text: 'Tokeny i Wydarzenia z wcześniejszych modułów',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dodatek rozbudowuje system tokenów i dodaje kolejne Wydarzenia. Tokeny Dzicz, Choroba, Konflikt i Bestie działają według zasad opisanych w rozdziale Zarośla i Szpony, o ile aktywny moduł je wprowadza. Nie łącz Scenariuszy i Przeciwników bez sprawdzenia kompatybilności na komponentach.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Moc zadaje 2 obrażenia Najeźdźcom w krainie ze Złą Ziemią. Zła Ziemia zwiększa te obrażenia wobec Najeźdźców (i lokalnie Dahan, jeśli akcja ich dotyczy), ale gdy ten sam obszar dostaje Odwet, obrażenia liczone do Zarazy krainy nie rosną przez Złą Ziemię. Osobno karta z Izolacją zmienia, które krainy sąsiadują dla źródła Eksploracji — sprawdzacie sąsiedztwo według tego efektu, nie „na pamięć” ze zwykłej mapy.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: doliczanie Złej Ziemi do obrażeń zadawanych krainie przy Odwecie.',
      },
      {
        type: 'tip',
        text: 'Nowi Duchowie mają własne przygotowanie i reguły specjalne z pierwszeństwem przed zasadami ogólnymi — odczytaj planszę. Przeciwnicy i Scenariusze — rozdział 19.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zła Ziemia wzmacnia obrażenia akcji wobec Najeźdźców/Dahan, nie wobec krainy; Izolacja zmienia sąsiedztwo i źródła według efektu. P: Czy Izolacja blokuje zasięg Duchów? O: Zwykle nie. P: Czy katalog nowych Duchów jest complete w aplikacji? O: Nie — PARTIAL / DO WERYFIKACJI.',
      },
    ],
  },
  {
    title: 'Pióra i Płomienie',
    requiredAddons: ['feather-and-flame'],
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tym dodatku',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Pióra i Płomienie zbiera dodatkowe Duchy i zawartość promocyjną. Ten rozdział uczy, jak włączyć tę zawartość do zasad ogólnych — bez wymyślania tekstów poszczególnych Duchów.',
      },
      {
        type: 'heading',
        text: 'Jak grać zawartością z tego zbioru?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Każdy Duch ma własne przygotowanie i regułę specjalną, które mają pierwszeństwo przed zasadami ogólnymi. Po wyborze Ducha wykonaj dokładnie instrukcję z jego planszy, weź unikalne Moce i znaczniki Obecności, potem stosuj zwykłą strukturę rundy z rozdziału 5.',
      },
      {
        type: 'heading',
        text: 'Granica źródeł',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Indywidualne teksty Duchów, Mocy i ewentualnych kart towarzyszących wymagają weryfikacji na komponentach. W aplikacji pozostają oznaczone jako niepełne, dopóki katalog nie zostanie uzupełniony. Nie nadawaj im statusu ready.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gosia wybiera Ducha z Piór i Płomieni. Najpierw czyta regułę specjalną i setup z planszy — ma pierwszeństwo, jeśli coś wygląda inaczej niż „zwykły” Duch z podstawy. Dopiero potem dołącza do wspólnego przygotowania mapy i talii Najeźdźców.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: pominięcie specjalnego przygotowania Ducha i granie „jak standardowym” torem Obecności wbrew planszy.',
      },
      {
        type: 'tip',
        text: 'Zasady ogólne Mocy, Obecności i Najeźdźców nadal obowiązują, o ile plansza Ducha ich nie zmienia. Katalog Duchów: [DO WERYFIKACJI].',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'To zbiór Duchów i promo; plansza Ducha wygrywa z ogólnymi zasadami. P: Czy mogę uznać opis Ducha za complete bez komponentu? O: Nie. P: Czy struktura rundy się zmienia? O: Tylko jeśli konkretny Duch lub karta tak stanowi.',
      },
    ],
  },
  {
    title: '19. Przeciwnicy i Scenariusze',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przeciwnicy i Scenariusze zmieniają trudność i cele partii. Ten rozdział podaje reguły ogólne ze źródła. Każdy poziom Przeciwnika i każdy Scenariusz wymaga osobnego, zweryfikowanego wpisu z komponentu — [DO WERYFIKACJI: katalog kart].',
      },
      {
        type: 'heading',
        text: 'Przeciwnik',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przeciwnik zmienia przygotowanie i zachowanie Najeźdźców. Poziomy kumulują się: grając na poziomie 4, stosujesz także poziomy 1–3, chyba że karta mówi inaczej.',
      },
      {
        type: 'heading',
        text: 'Scenariusz',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Scenariusz wprowadza własny cel, setup lub strukturę gry. Nie łącz modułów bez sprawdzenia kompatybilności na materiałach dodatku.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: stosowanie tylko „najwyższego” poziomu Przeciwnika bez poziomów niższych, gdy karta wymaga kumulacji.',
      },
      {
        type: 'tip',
        text: 'Na pierwszą grę nie używaj Przeciwnika ani Scenariusza — rozdział 4 i First Game.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Poziomy Przeciwnika zwykle się kumulują; Scenariusz zmienia cel lub strukturę. P: Czy aplikacja zawiera gotowy katalog wszystkich Przeciwników? O: Nie — PARTIAL. P: Czy wolno mieszać dowolny Scenariusz z dowolnym Przeciwnikiem? O: Tylko po sprawdzeniu kompatybilności.',
      },
    ],
  },
  {
    title: '20. Koniec rundy i sprawdzanie zwycięstwa',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Po Fazie Wolnych Mocy zamykasz rundę: sprawdzasz zwycięstwo i porażkę po pełnych efektach, a potem wykonujesz Upływ Czasu. Pomylenie tej kolejności zostawia „wiszące” Moce albo fałszywe wygrane w środku nierozpatrzonej akcji.',
      },
      {
        type: 'heading',
        text: 'Kiedy i w jakiej kolejności?',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Dokończ Fazę Wolnych Mocy — każda wolna Moc musi być w pełni rozpatrzona albo legalnie pominięta z braku celu.',
          'Sprawdź warunki zwycięstwa i porażki obowiązujące przy bieżącym Poziomie Terroru (rozdział 2).',
          'Wykonaj Upływ Czasu: odrzuć zagrane w tej rundzie karty Mocy, usuń znaczniki jednorundowych efektów, odwróć lub przywróć elementy zgodnie z ich zasadami.',
          'Jeśli partia trwa, przejdź do kolejnej rundy od Fazy Duchów.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Zwycięstwo sprawdzasz także po innych w pełni rozpatrzonych efektach w trakcie rundy (np. po karcie Strachu), nie tylko na końcu — zawsze najpierw dokończ efekt, potem sprawdź warunek, chyba że karta mówi inaczej. Jeśli zwycięstwo i porażka wynikają z tego samego w pełni rozpatrzonego efektu, zastosuj oficjalne reguły kolejności dla danej sytuacji i wpis FAQ. Edge cases aktualnego FAQ: [DO WERYFIKACJI].',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Jesteście na Terrorze III. Wolna Moc Anny niszczy ostatnie Miasto na wyspie. Po zakończeniu tej Mocy sprawdzacie zwycięstwo: nie ma Miast, więc wygrywacie natychmiast — nie zaczynacie już Upływu Czasu ani kolejnej rundy. Gdyby ta sama Moc jednocześnie wyczerpała pulę Zarazy w sytuacji wymagającej dodania Zarazy, rozstrzygnięcie „wygrana czy przegrana z tego samego efektu” wymaga oficjalnego FAQ: [DO WERYFIKACJI].',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: rozpoczęcie nowej rundy bez odrzucenia zagranych Mocy albo bez usunięcia znaczników jednorundowych. Drugi błąd: ogłoszenie wygranej w środku nierozpatrzonej karty.',
      },
      {
        type: 'tip',
        text: 'Pełny zegar faz — rozdział 5. Warunki Terroru i porażki — rozdział 2. Strach — rozdział 14.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dokończ wolne Moce → sprawdź koniec po pełnym efekcie → Upływ Czasu albo zakończ partię. P: Czy można ogłosić wygraną w środku Odwetu? O: Najpierw dokończ efekt, potem sprawdź, chyba że karta mówi inaczej. P: Czy Upływ Czasu jest potrzebny po zwycięstwie? O: Nie — partia się kończy.',
      },
    ],
  },
  {
    title: '21. Pierwsza partia — przewodnik',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'To ścieżka pierwszej wspólnej partii bez Przeciwnika, Scenariusza i Wydarzeń. Cel: zrozumieć zegar rundy, zanim dodasz chaos dodatków.',
      },
      {
        type: 'step-list',
        steps: [
          'Wybierz Duchy o niskiej złożoności.',
          'Nie używaj Przeciwnika, Scenariusza ani Wydarzeń.',
          'Rozłóż jedną planszę na gracza.',
          'W pierwszej Fazie Duchów przeczytaj wszystkie opcje Wzrostu na głos.',
          'Najpierw zaplanuj Szybkie Moce, które zapobiegną Budowie lub Odwetowi.',
          'Wolne Moce traktuj jako przygotowanie następnej rundy.',
          'Przed Fazą Najeźdźców wskaż wszystkie krainy Odwetu, Budowy i Eksploracji.',
          'Po pierwszej rundzie (w kolejnej partii) dodaj Wydarzenia.',
        ],
      },
      {
        type: 'warning',
        text: 'Typowy błąd: włączenie Wydarzeń i Przeciwnika od razu w pierwszej nauce — utrudnia naukę podstawowego zegara faz.',
      },
      {
        type: 'tip',
        text: 'Ten sam przebieg prowadzi First Game w aplikacji. Po opanowaniu podstaw włącz Zarośla i Szpony jednym rozdziałem dodatku.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Prosta mapa, proste Duchy, zero Wydarzeń i Przeciwnika, głośny Wzrost, najpierw szybkie Moce. P: Kiedy dodać Wydarzenia? O: W kolejnej partii, nie w pierwszej rundzie pierwszej nauki.',
      },
    ],
  },
  {
    title: 'QUICK REFERENCE',
    content: [
      {
        type: 'heading',
        text: 'Kolejność rundy',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Wzrost.',
          'Energia.',
          'Zagrywanie i opłacanie kart.',
          'Szybkie Moce.',
          'Wydarzenie od rundy 2 (gdy aktywne).',
          'Strach (rozpatrz zdobyte karty).',
          'Odwet.',
          'Budowa.',
          'Eksploracja.',
          'Przesunięcie kart Najeźdźców.',
          'Wolne Moce.',
          'Upływ Czasu.',
        ],
      },
      {
        type: 'heading',
        text: 'Najeźdźcy — Zdrowie / obrażenia / Strach za zniszczenie',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Odkrywca: 1 Zdrowia, 1 obrażenie; źródło nie przypisuje Strachu za zniszczenie.',
          'Osada: 2 Zdrowia, 2 obrażenia; zniszczenie → 1 Strach.',
          'Miasto: 3 Zdrowia, 3 obrażenia; zniszczenie → 2 Strachu.',
          'Obrażenia na Najeźdźcach nie przenoszą się między akcjami.',
        ],
      },
      {
        type: 'heading',
        text: 'Terror — warunek zwycięstwa',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'I: brak wszelkich Najeźdźców.',
          'II: brak Osad i Miast.',
          'III: brak Miast.',
          'IV: natychmiastowe zwycięstwo.',
        ],
      },
      {
        type: 'heading',
        text: 'Odwet i Dahanie w skrócie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Suma obrażeń Najeźdźców − Obrona → jednocześnie kraina i Dahanie. Przy ≥2 obrażeniach krainy dodaj 1 Zarazę (nie więcej z samej siły ciosu). Obrażenia Dahan: niszcz jak najwięcej (po 2); ranni do Upływu Czasu. Ocalali kontratakują po 2 każdy. Dahanie bez własnej tury; Zbierz/Wypchnij tylko z efektu. Szczegóły — rozdział 10.',
      },
      {
        type: 'heading',
        text: 'Budowa / Eksploracja',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Budowa (kraina z Najeźdźcami): więcej Miast niż Osad → Miasto; inaczej Osada. Eksploracja: Odkrywca tylko ze źródłem (ocean, sąsiednia Osada/Miasto lub reguła Przeciwnika) — nie z samego Odkrywcy.',
      },
    ],
  },
  {
    title: 'NAJCZĘSTSZE BŁĘDY',
    content: [
      {
        type: 'paragraph',
        text: 'Poniższe pomyłki pochodzą ze źródłowego szkicu instrukcji. Przy każdej popraw regułę.',
      },
      {
        type: 'step-list',
        steps: [
          'Zużywanie elementów po sprawdzeniu progu — elementy się nie wydaje.',
          'Dodawanie wielu Zaraz za jeden bardzo silny Odwet — przy ≥2 obrażeniach krainy dodaj 1 Zarazę.',
          'Pozostawianie obrażeń na Najeźdźcach między akcjami — po akcji wracają do pełnego Zdrowia.',
          'Kontratak Dahan przed obrażeniami — najpierw obrażenia, potem kontratak ocalałych.',
          'Dawanie Dahanom własnej tury ruchu — działają tylko przez Odwet, Moce i sekcje kart.',
          'Rozpraszanie obrażeń Odwetu po 1 na wielu Dahan, by uniknąć zniszczenia — niszczysz jak najwięcej.',
          'Resetowanie ran Dahan po każdej akcji jak u Najeźdźców — ranni Dahanie leczą się w Upływie Czasu.',
          'Zbieranie Dahan z krain nie sąsiadujących z celem Mocy albo mierzenie zasięgu do źródła zamiast do celu.',
          'Traktowanie Bestii jak jednostek walczących bez efektu karty.',
          'Eksploracja z sąsiedniego Odkrywcy — źródłem jest Osada, Miasto lub ocean.',
          'Rozpatrywanie Wydarzenia w pierwszej rundzie.',
          'Zapominanie o dodatkowej Zarazie z aktualnej korekty setupu.',
          'Liczenie Obecności innych Duchów jako własnego Świętego Miejsca.',
        ],
      },
    ],
  },
  {
    title: 'FAQ',
    content: [
      {
        type: 'paragraph',
        text: 'Czy można zagrywać szybkie i wolne Moce w dowolnej kolejności? Tylko wewnątrz odpowiedniej fazy. Wszystkie szybkie rozpatruje się przed Fazą Najeźdźców, a wolne po niej.',
      },
      {
        type: 'paragraph',
        text: 'Czy elementy się wydaje? Nie. Elementy są symbolami posiadanymi w danej rundzie.',
      },
      {
        type: 'paragraph',
        text: 'Czy jedna karta może spełnić kilka progów? Tak, o ile posiadasz wymagane elementy.',
      },
      {
        type: 'paragraph',
        text: 'Czy Obrona broni Dahan i krainy? Tak. Ta sama wartość redukuje łączny pakiet obrażeń Najeźdźców przed zadaniem go krainie i Dahanom.',
      },
      {
        type: 'paragraph',
        text: 'Czy Dahanie mają własną turę? Nie. Nie ma fazy Dahan — reagują na Odwet, Moce (w tym Zbierz/Wypchnij) oraz sekcje kart takich jak Wydarzenia.',
      },
      {
        type: 'paragraph',
        text: 'Czy ranny Dahan (1 obrażenie w Odwecie) kontratakuje? Tak, jeśli nie został zniszczony. Każdy ocalały zadaje 2. Obrażenia na Dahan rozdzielasz tak, by zniszczyć ich jak najwięcej — nie rozpraszasz po 1, by uniknąć zniszczenia. Rozdział obrażeń kontrataku między Najeźdźców jest decyzją drużyny.',
      },
      {
        type: 'paragraph',
        text: 'Czy Obrona działa na obrażenia z Mocy lub Wydarzeń? Tylko jeśli efekt mówi, że są to obrażenia Najeźdźców podczas Odwetu albo wyraźnie rozszerza działanie.',
      },
      {
        type: 'paragraph',
        text: 'Czy przesunięcie Najeźdźcy niszczy go? Nie. Zmienia tylko jego krainę.',
      },
      {
        type: 'paragraph',
        text: 'Czy Osada generuje Strach przy usunięciu bez „zniszczenia”? Zależy od użytego czasownika efektu. Katalog terminów musi rozróżniać „zniszcz”, „usuń” i „zastąp”.',
      },
      {
        type: 'paragraph',
        text: 'Czy można wybrać cel bez efektu? Tak tylko wtedy, gdy cel jest legalny. Nie wolno łamać warunków celu.',
      },
      {
        type: 'paragraph',
        text: 'Czy Zaraza niszczy wszystkie Obecności? Niszczy po jednej Obecności każdego Ducha w tej krainie, o ile efekt nie zmienia zasady.',
      },
      {
        type: 'paragraph',
        text: 'Czy Dzicz blokuje Eksplorację wywołaną kartą Mocy? Nie automatycznie. Blokuje normalną akcję Eksploracji Najeźdźców, chyba że efekt mówi inaczej.',
      },
    ],
  },
  {
    title: 'GLOSSARY',
    content: [
      {
        type: 'step-list',
        steps: [
          'Dahan — rdzenni mieszkańcy wyspy i sojusznicy Duchów; bez własnej tury; 2 Zdrowia; przy obrażeniach niszczysz jak najwięcej; ranni do Upływu Czasu; kontratak po Odwecie; Zbierz/Wypchnij tylko z efektu.',
          'Zbierz (Gather) — przenieś wskazane figurki z sąsiednich krain do krainy-celu Mocy (zasięg Mocy nie wydłuża dystansu zbierania).',
          'Wypchnij (Push) — przenieś wskazane figurki z krainy-celu do sąsiednich krain (różne figurki mogą iść w różne sąsiedztwa).',
          'Element — symbol na karcie lub torze, używany do spełniania progów.',
          'Energia — zasób z toru Energii (po Wzroście), którym opłacasz koszt Mocy.',
          'Obecność — znacznik Ducha na wyspie, źródło zasięgu.',
          'Święte Miejsce — zwykle kraina z co najmniej 2 Obecnościami jednego Ducha.',
          'Odwet — akcja Najeźdźców zadająca obrażenia krainie i Dahanom.',
          'Budowa — akcja dodająca Osadę albo Miasto.',
          'Eksploracja — akcja dodająca Odkrywcę do krainy ze źródłem.',
          'Strach — postęp prowadzący do kart Strachu i wyższych poziomów Terroru.',
          'Zaraza — trwałe uszkodzenie wyspy; kaskaduje przy drugiej Zarazie w krainie.',
          'Wyspa Spustoszona — odwrócona karta Wyspy po wyczerpaniu puli Zdrowa; sprawdzana na początku Fazy Najeźdźców.',
          'Zasięg 0 — cel w krainie zawierającej źródłową Obecność.',
        ],
      },
    ],
  },
];

export const spiritIslandFirstGame: FirstGameStep[] = [
  {
    id: 1,
    title: 'Wybierz proste Duchy',
    content: [
      'Wybierz Duchy o niskiej złożoności.',
      'Weź planszę, unikalne Moce i znaczniki Obecności każdego Ducha.',
    ],
    why: 'Proste Duchy uczą Wzrostu, Obecności i Mocy bez specjalnych wyjątków planszy.',
    commonMistake: 'Wybór Duchów o wysokiej złożoności na pierwszą naukę.',
    chapterIndex: 20,
  },
  {
    id: 2,
    title: 'Bez Przeciwnika i Wydarzeń',
    content: [
      'Nie używaj Przeciwnika, Scenariusza ani Wydarzeń.',
    ],
    why: 'Najpierw opanuj zegar faz podstawy; Wydarzenia dodasz w kolejnej partii.',
    commonMistake: 'Włączenie Wydarzeń już w pierwszej rundzie pierwszej partii.',
    chapterIndex: 20,
  },
  {
    id: 3,
    title: 'Rozłóż mapę',
    content: [
      'Rozłóż jedną planszę wyspy na gracza i ustaw figurki według ikon.',
      'Pamiętaj o +1 Zarazie względem samego mnożnika liczby graczy.',
    ],
    why: 'Mapa i pula Zarazy muszą być poprawne, zanim padnie pierwszy Odwet.',
    commonMistake: 'Zapomnienie korekty +1 Zarazy w setupie.',
    chapterIndex: 3,
  },
  {
    id: 4,
    title: 'Głośny Wzrost',
    content: [
      'W pierwszej Fazie Duchów przeczytaj wszystkie opcje Wzrostu na głos.',
    ],
    why: 'Cały stół musi widzieć, co odsłaniają tory Energii i kart.',
    commonMistake: 'Ciche wybory Wzrostu bez sprawdzenia legalności zasięgu Obecności.',
    chapterIndex: 5,
  },
  {
    id: 5,
    title: 'Najpierw szybkie Moce',
    content: [
      'Najpierw zaplanuj Szybkie Moce, które zapobiegną Budowie lub Odwetowi.',
    ],
    why: 'Obrona i usuwanie zagrożeń muszą zdążyć przed Fazą Najeźdźców.',
    commonMistake: 'Trzymanie Obrony na wolnej Mocy, gdy Odwet jest w tej rundzie.',
    chapterIndex: 4,
  },
  {
    id: 6,
    title: 'Wolne Moce jako przygotowanie',
    content: [
      'Wolne Moce traktuj jako przygotowanie następnej rundy.',
    ],
    why: 'Po Najeźdźcach sprzątasz i ustawiasz Obecność pod kolejny Wzrost.',
    commonMistake: 'Oczekiwanie, że wolna Moc zdąży przed Odwetem.',
    chapterIndex: 4,
  },
  {
    id: 7,
    title: 'Wskaż krainy Najeźdźców',
    content: [
      'Przed Fazą Najeźdźców wskaż wszystkie krainy Odwetu, Budowy i Eksploracji.',
    ],
    why: 'Wspólne wskazanie krain zapobiega pominięciu Odwetu albo Budowy.',
    commonMistake: 'Eksploracja z sąsiedniego Odkrywcy zamiast ze źródła Osada/Miasto/ocean.',
    chapterIndex: 4,
  },
  {
    id: 8,
    title: 'Wydarzenia później',
    content: [
      'Po opanowaniu pierwszej partii dodaj Wydarzenia dopiero w kolejnej grze.',
    ],
    why: 'Wydarzenia od rundy 2 zmieniają Fazę Najeźdźców — najpierw naucz się podstawy.',
    commonMistake: 'Rozpatrywanie Wydarzenia w pierwszej rundzie.',
    chapterIndex: 20,
  },
];
